import { Button, SimplePagination, Table } from '@dixa/lib-shared-habla';
import { AsyncStatus, QueueAgent } from 'lib-shared-legacy-types';
import { PureComponent } from 'react';
import { FieldArray, InjectedFormProps } from 'redux-form';

import { TableLoadingRow } from 'components/shared/tableLoadingRow';

import styles from '../queues.module.scss';
import { QueuesAgentsTableRow } from '../queuesAgentsTableRow';

export type QueuesAgentsTableOwnProps = {
  queueOfferAlgorithm?: string;
  getItemsAsyncStatus: AsyncStatus;
  items: QueueAgent[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  showRemoveModal: (queueAgent: QueueAgent) => void;
  handleRetry: () => void;
  gotoNextPage: () => void;
  gotoPreviousPage: () => void;
  isLoading: boolean;
};

export type QueuesAgentsTableDispatchProps = {};

export type QueuesAgentsTableStateProps = {};

type QueuesAgentsTablePriority = {
  agent_id: string;
  priority: number;
};

export type QueuesAgentsTableFormValues = {
  agents: QueuesAgentsTablePriority[];
};

type QueuesAgentsTableProps = QueuesAgentsTableOwnProps &
  InjectedFormProps<QueuesAgentsTableFormValues, QueuesAgentsTableOwnProps>;

type QueuesAgentsTableState = {};

export class QueuesAgentsTable extends PureComponent<
  QueuesAgentsTableProps,
  QueuesAgentsTableState
> {
  getNumberOfColumns(queueOfferAlgorithm) {
    return queueOfferAlgorithm ? 4 : 3;
  }

  renderLoadingStatus() {
    const { getItemsAsyncStatus, queueOfferAlgorithm, handleRetry } =
      this.props;

    if (getItemsAsyncStatus === 'failed') {
      return (
        <Table.Tr>
          <Table.Td colSpan={this.getNumberOfColumns(queueOfferAlgorithm)}>
            Something went wrong...
            <Button variant="tertiary" onClick={handleRetry}>
              Retry
            </Button>
          </Table.Td>
        </Table.Tr>
      );
    }
    return (
      <TableLoadingRow colSpan={this.getNumberOfColumns(queueOfferAlgorithm)} />
    );
  }

  renderSuccessStatus() {
    const { items, queueOfferAlgorithm, showRemoveModal } = this.props;

    if (items.length) {
      return (
        <FieldArray
          name="agents"
          component={QueuesAgentsTableRow}
          queueOfferAlgorithm={queueOfferAlgorithm}
          showRemoveModal={showRemoveModal}
          items={items}
        />
      );
    }

    return (
      <Table.Tr>
        <Table.Td colSpan={this.getNumberOfColumns(queueOfferAlgorithm)}>
          No agents found.
        </Table.Td>
      </Table.Tr>
    );
  }

  renderFoot() {
    const {
      hasNextPage,
      hasPreviousPage,
      gotoNextPage,
      gotoPreviousPage,
      getItemsAsyncStatus,
      pristine,
      submitting,
      queueOfferAlgorithm,
    } = this.props;

    const foot = (
      <Table.Tfoot>
        <Table.Tr>
          <Table.Td colSpan={this.getNumberOfColumns(queueOfferAlgorithm)}>
            <SimplePagination
              previousButtonClick={gotoPreviousPage}
              previousButtonDisabled={
                !pristine || submitting || !hasPreviousPage
              }
              nextButtonClick={gotoNextPage}
              nextButtonDisabled={!pristine || submitting || !hasNextPage}
              loading={getItemsAsyncStatus === 'loading'}
            />
          </Table.Td>
        </Table.Tr>
      </Table.Tfoot>
    );

    return foot;
  }

  render() {
    const {
      queueOfferAlgorithm,
      handleSubmit,
      pristine,
      submitting,
      getItemsAsyncStatus,
      hasNextPage,
      hasPreviousPage,
      isLoading,
    } = this.props;

    const showFoot = hasNextPage || hasPreviousPage;

    return (
      <form className={styles.tableForm} onSubmit={handleSubmit}>
        <Table framed stickyHeader stickyFooter headerWithBackground>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Agent(s)</Table.Th>

              <Table.Th width="35%">Email</Table.Th>

              {queueOfferAlgorithm?.includes('agent_priority') && (
                <Table.Th>Priority</Table.Th>
              )}

              <Table.Th className={styles.headCellActions}>
                {queueOfferAlgorithm?.includes('agent_priority') && (
                  <Button disabled={pristine || submitting} type="submit">
                    Save
                  </Button>
                )}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {!isLoading && getItemsAsyncStatus === 'succeeded'
              ? this.renderSuccessStatus()
              : this.renderLoadingStatus()}
          </Table.Tbody>

          {showFoot && this.renderFoot()}
        </Table>
      </form>
    );
  }
}
